import { isNotDefined } from '@sgme/fp';

import { useOptionSelector } from '@/App/tiles/Option/OptionContext.ts';
import { useCheckOption } from '@/App/tiles/Option/QueueContext.tsx';
import { LiveUpdatableInput } from '@/App/tiles/Option/strategy/fields/LiveUpdatableInput.tsx';
import { selectOptionCurrency, selectOptionHedge, selectQuoteHedge } from '@/store/option/selectors/optionSelectors.ts';
import type { LegId } from '@/store/option/state.ts';

type HedgeAmountProps = {
  tileId: string;
  legId: LegId;
  className?: string;
};

export function HedgeAmount({ tileId, legId, className }: HedgeAmountProps) {
  const [checkOption] = useCheckOption();
  const { quoteHedge, currencyLabel, hedge } = useOptionSelector((option) => {
    const quoteHedge = selectQuoteHedge(option, legId);
    const { currency1, currency2 } = selectOptionCurrency(option);
    const hedge = selectOptionHedge(option, legId);
    const currencyLabel = hedge?.notionalCurrency === 2 ? currency2 : currency1;
    return {
      quoteHedge,
      currencyLabel,
      hedge,
    };
  });

  if (isNotDefined(hedge)) {
    return <div />;
  }

  function onValueChange(hedgeNotional: number | undefined | null): void {
    if (isNotDefined(hedge)) {
      return;
    }

    checkOption({
      tileId,
      hedges: { legId: hedge.legId, notionalAmount: hedgeNotional, notionalCurrency: hedge.notionalCurrency ?? 1 },
    });
  }

  function onCurrencyChange(): void {
    if (isNotDefined(hedge)) {
      return;
    }

    const notionalCurrency = hedge.notionalCurrency === 1 ? 2 : 1;
    checkOption({
      tileId,
      hedges: { legId: hedge.legId, notionalAmount: hedge.notionalAmount, notionalCurrency: notionalCurrency },
    });
  }

  return (
    <LiveUpdatableInput
      data-e2e="HedgeNotional"
      liveValue={quoteHedge?.notionalAmount}
      editValue={hedge.notionalAmount}
      onChange={onValueChange}
      unit={currencyLabel}
      onUnitClick={onCurrencyChange}
      unitBackgroundColor={hedge.notionalCurrency === 2 ? 'warning' : undefined}
      className={className}
    />
  );
}
